import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { addYears, startOfYear } from 'date-fns';
import Decimal from 'decimal.js';
import { Checkbox, MenuItem, TextField, Typography, WithInfoIcon, InlineSelect, InlineSelectDatePicker } from 'seagull';
import { getPriceIncreaseDateRange } from "../../../business-logic/priceIncreaseLogic";
import { useIoContext } from "../../../contexts";
import { decimalFromStr } from "../../../infra/utils";
import { serviceModel } from "../../../models";
import { PriceIncreaseModel, RoundPrices } from "../../../models/PriceIncreaseModel";
import { utils } from "../../../utils";
import { AdvancedSettingsControl } from "../AdvancedSettingsControl/AdvancedSettingsControl";
import { AdvancedSetting, IncreaseMethod } from "../ServiceAdvancedSettings.types";
import { getInitialIncreaseMethod, getValidIncreaseOn } from "../ServiceAdvancedSettings.utils";
import { TextAndInlineSelectRow } from "../TextAndInlineSelectRow";
import styles from "./AutoPriceIncreaseControl.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const increaseMethodToText = {
  [IncreaseMethod.EffectiveDate]: 'on agreement anniversary',
  [IncreaseMethod.Date]: 'starting on'
};
const increaseMethodOptions = [{
  value: IncreaseMethod.EffectiveDate
}, {
  value: IncreaseMethod.Date
}];
const roundPricesOptions = [{
  value: RoundPrices.Down1,
  amount: 1
}, {
  value: RoundPrices.Down5,
  amount: 5
}, {
  value: RoundPrices.Down10,
  amount: 10
}];
export function AutoPriceIncreaseControl(props) {
  const {
    service,
    info,
    label,
    showServiceValidationWarning,
    relationshipState,
    isReadOnly,
    onAutoPriceIncreaseChange,
    testId: testPrefix
  } = props;
  const disableIncreaseOnAnniversary = relationshipState.type === 'approved';
  const priceIncrease = serviceModel.priceIncrease(service);
  const {
    getNow
  } = useIoContext();
  const [roundPrices, setRoundPrices] = useState(priceIncrease.roundPrices());
  const [increaseOn, setIncreaseOn] = useState(() => priceIncrease.increaseOn() || startOfYear(addYears(getNow(), 1)));
  const [increaseMethod, setIncreaseMethod] = useState(getInitialIncreaseMethod(priceIncrease, relationshipState));
  const [percentIncrease, setPercentIncrease] = useState(!priceIncrease.exists() || priceIncrease.percentIncrease().toNumber() < 0 ? '' : priceIncrease.percentIncrease().toString());
  useEffect(() => {
    const currentPriceIncrease = serviceModel.priceIncrease(service);
    setRoundPrices(currentPriceIncrease.roundPrices());
    setIncreaseOn(currentPriceIncrease.increaseOn() || startOfYear(addYears(getNow(), 1)));
    setIncreaseMethod(getInitialIncreaseMethod(currentPriceIncrease, relationshipState));
    setPercentIncrease(!currentPriceIncrease.exists() || currentPriceIncrease.percentIncrease().toNumber() < 0 ? '' : currentPriceIncrease.percentIncrease().toString());
  }, [service, relationshipState, getNow]);
  const nextYearPrice = serviceModel.nextYearPrice(service);
  const hasPriceIncrease = priceIncrease.exists();
  const shouldRoundPrices = roundPrices !== RoundPrices.None;
  const roundPricesError = showServiceValidationWarning && serviceModel.isInvalidRoundPrices(service) ? serviceModel.roundPricesErrorMsg : null;
  const relationshipEffectiveDate = relationshipState.effectiveDate;
  const increaseMethodError = serviceModel.isInvalidIncreaseMethod(increaseMethod === IncreaseMethod.Date ? increaseOn : null, relationshipEffectiveDate) ? "Only available when the effective date is 'On acceptance'" : null;
  const {
    minDate,
    maxDate
  } = useMemo(() => {
    return getPriceIncreaseDateRange(getNow(), relationshipEffectiveDate);
  }, [relationshipEffectiveDate]);
  function handleStateChange(data) {
    if (!onAutoPriceIncreaseChange) return;
    const {
      newIncreaseOn = increaseOn,
      newPercentIncrease = percentIncrease,
      newRoundPrices = roundPrices,
      newIncreaseMethod = increaseMethod
    } = data;
    const validNewIncreaseOn = getValidIncreaseOn(newIncreaseOn, getNow());
    setIncreaseOn(validNewIncreaseOn);
    setPercentIncrease(newPercentIncrease);
    setRoundPrices(newRoundPrices);
    setIncreaseMethod(newIncreaseMethod);
    const priceIncrease = data.enabled ? PriceIncreaseModel.make(newRoundPrices, decimalFromStr(newPercentIncrease) || new Decimal(-1), newIncreaseMethod === IncreaseMethod.Date ? validNewIncreaseOn : null) : PriceIncreaseModel.makeNone();
    onAutoPriceIncreaseChange(service.id, priceIncrease);
  }
  return /*#__PURE__*/_jsx(AdvancedSettingsControl, {
    info: hasPriceIncrease ? '' : info,
    isReadOnly: isReadOnly,
    label: hasPriceIncrease ? '' : label,
    checked: hasPriceIncrease,
    testId: "".concat(testPrefix, "-toggle-price-increase"),
    onChange: checked => handleStateChange({
      enabled: checked
    }),
    labelSuffix: hasPriceIncrease && /*#__PURE__*/_jsx(WithInfoIcon, {
      info: info,
      infoTestId: "".concat(testPrefix, "-price-increase-info"),
      children: /*#__PURE__*/_jsxs(TextAndInlineSelectRow, {
        children: [/*#__PURE__*/_jsx(Typography, {
          children: "Set an automatic annual price increase "
        }), disableIncreaseOnAnniversary ? /*#__PURE__*/_jsx(Typography, {
          children: "starting on "
        }) : /*#__PURE__*/_jsx(InlineSelect, {
          skin: "primary",
          value: increaseMethod,
          testId: "".concat(testPrefix, "-price-increase-method"),
          disabled: isReadOnly,
          error: !!increaseMethodError,
          helperText: increaseMethodError,
          SelectProps: {
            renderValue: () => increaseMethodToText[increaseMethod]
          },
          onChange: e => handleStateChange({
            enabled: true,
            newIncreaseMethod: e.target.value
          }),
          children: increaseMethodOptions.map(_ref => {
            let {
              value
            } = _ref;
            return /*#__PURE__*/_jsx(MenuItem, {
              value: value,
              testId: "".concat(testPrefix, "-price-increase-method-").concat(value),
              children: increaseMethodToText[value]
            }, value);
          })
        }), increaseMethod === IncreaseMethod.Date && increaseOn && /*#__PURE__*/_jsx(InlineSelectDatePicker, {
          size: "small",
          testId: "".concat(testPrefix, "-price-increase-date"),
          value: increaseOn,
          minDate: minDate,
          maxDate: maxDate,
          disabled: isReadOnly,
          disableErrors: !showServiceValidationWarning,
          skin: "primary",
          format: "MMM d, yyyy",
          onChange: newIncreaseOn => newIncreaseOn && handleStateChange({
            enabled: true,
            newIncreaseOn
          })
        })]
      })
    }),
    children: hasPriceIncrease && /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.additionalControl,
        children: [/*#__PURE__*/_jsx(TextField, {
          collapseHelperText: false,
          isMoney: true,
          disableMobile: true,
          testId: "".concat(testPrefix, "-price-increase-amount"),
          label: "increase by",
          inputProps: {
            min: 0
          },
          disabled: isReadOnly,
          size: "small",
          suffix: "%",
          width: 150,
          value: percentIncrease,
          onChange: e => handleStateChange({
            enabled: true,
            newPercentIncrease: e.target.value
          }),
          error: showServiceValidationWarning && serviceModel.isInvalidPercentIncrease(percentIncrease)
        }), /*#__PURE__*/_jsxs(Typography, {
          variant: "paragraph1",
          testId: "".concat(testPrefix, "-price-increase-next-year"),
          as: "span",
          children: ["Next year's price:", ' ', nextYearPrice ? /*#__PURE__*/_jsx(Typography, {
            as: "span",
            weight: "bold",
            children: utils.formatDollarAmount(nextYearPrice)
          }) : '']
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: cn(styles.additionalControl, styles.roundPrices),
        children: [/*#__PURE__*/_jsx(Checkbox, {
          checked: shouldRoundPrices,
          disabled: isReadOnly,
          testId: "".concat(testPrefix, "-price-increase-should-round-prices"),
          onChange: e => handleStateChange({
            enabled: true,
            newRoundPrices: e.target.checked ? RoundPrices.Down1 : RoundPrices.None
          })
        }), shouldRoundPrices ? /*#__PURE__*/_jsx(WithInfoIcon, {
          info: "Automatically rounds prices down to the nearest whole number for customer-friendly pricing.",
          className: styles.rowAndIconContainer,
          children: /*#__PURE__*/_jsxs("div", {
            className: styles.rowContent,
            children: [/*#__PURE__*/_jsx(Typography, {
              children: "Round prices"
            }), /*#__PURE__*/_jsx(InlineSelect, {
              skin: "primary",
              testId: "".concat(testPrefix, "-price-increase-round-prices"),
              disabled: isReadOnly,
              helperText: roundPricesError,
              value: roundPrices,
              error: !!roundPricesError,
              onChange: e => handleStateChange({
                enabled: true,
                newRoundPrices: e.target.value
              }),
              children: roundPricesOptions.map(_ref2 => {
                let {
                  value,
                  amount
                } = _ref2;
                return /*#__PURE__*/_jsxs(MenuItem, {
                  value: value,
                  testId: "".concat(testPrefix, "-price-increase-round-prices-").concat(value),
                  children: ["down to the nearest ", utils.formatDollarAmount(amount)]
                }, value);
              })
            })]
          })
        }) : /*#__PURE__*/_jsx(WithInfoIcon, {
          info: "Automatically rounds prices down to the nearest whole number for customer-friendly pricing.",
          children: /*#__PURE__*/_jsx(Typography, {
            children: "Round prices "
          })
        })]
      })]
    })
  }, AdvancedSetting.AutoPriceIncrease);
}